import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import {
  ArrowRight,
  Bot,
  Calendar,
  CreditCard,
  Globe2,
  Layers,
  MessageSquare,
  Network,
  ShieldCheck,
  Sparkles,
  Zap,
  Check,
} from "lucide-react";
import { useT, useMedia } from "@/lib/i18n";
import agenteHero from "@/assets/agente-hero.jpg";

const AGENTE_URL = "https://agente.pulso33.com/";

export const Route = createFileRoute("/products/agente")({
  component: AgenteProductPage,
  head: () => ({
    meta: [
      { title: "AgentE — The first SaaS by PULSO33" },
      {
        name: "description",
        content:
          "AgentE is PULSO33's first SaaS product: a 24/7 AI sales assistant, website, CRM, bookings, payments and a marketplace — one platform reimagining how service businesses sell online.",
      },
      { property: "og:title", content: "AgentE — by PULSO33" },
      {
        property: "og:description",
        content:
          "An AI sales agent, website, CRM, bookings, payments and a professional marketplace — in a single platform. Live in 5 days.",
      },
      { property: "og:url", content: "https://pulso33.com/products/agente" },
      { property: "og:image", content: "https://pulso33.com/src/assets/agente-hero.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://pulso33.com/products/agente" }],
  }),
});

function AgenteProductPage() {
  const { tk } = useT();
  const hero = useMedia("products.agente.hero", agenteHero, "AgentE — AI sales platform by PULSO33");

  const features = [
    { i: Bot, t: tk("agente.f1.t", "24/7 AI sales agent"), d: tk("agente.f1.d", "Trained on your catalog, prices and tone. Detects intent, recommends the right package and guides the customer to booking or checkout.") },
    { i: Globe2, t: tk("agente.f2.t", "Multilingual website"), d: tk("agente.f2.d", "Headless CMS with automatic translations. Your brand, your domain, white-label from day one.") },
    { i: Layers, t: tk("agente.f3.t", "Integrated CRM"), d: tk("agente.f3.d", "Leads, pipeline and follow-ups centralized — no more lost conversations between WhatsApp, email and spreadsheets.") },
    { i: Calendar, t: tk("agente.f4.t", "Bookings & calendar"), d: tk("agente.f4.d", "Public calendar with email confirmations. The assistant books for you while you sleep.") },
    { i: CreditCard, t: tk("agente.f5.t", "Stripe payments"), d: tk("agente.f5.d", "Products, packages and recurring billing. Sell services online in two clicks.") },
    { i: Network, t: tk("agente.f6.t", "Professional marketplace"), d: tk("agente.f6.d", "Private networks and a verified marketplace of specialists — never turn down a client again. Split payments handled automatically.") },
  ];

  const stats = [
    { v: "4h", l: tk("agente.stat.setup", "Initial setup") },
    { v: "5d", l: tk("agente.stat.onboarding", "Full onboarding") },
    { v: "24/7", l: tk("agente.stat.ai", "AI on the front line") },
  ];

  const included = [
    tk("agente.inc.1", "Full platform — web, CRM, bookings, payments, blog"),
    tk("agente.inc.2", "AgentE assistant + SEO learning chat"),
    tk("agente.inc.3", "Collaboration network + verified marketplace"),
    tk("agente.inc.4", "Branding, custom domain and Stripe + email setup"),
    tk("agente.inc.5", "Continuous updates and new modules included"),
  ];

  return (
    <Layout>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-gold/15">
        <img
          src={hero.url}
          alt={hero.alt}
          aria-hidden
          className="absolute inset-0 w-full h-full object-cover opacity-45"
          fetchPriority="high"
          width={1920}
          height={1080}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background/85 to-background/60" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-32">
          <div className="inline-flex items-center gap-3 mb-8">
            <span className="gold-rule" />
            <span className="text-xs tracking-[0.4em] uppercase text-gold">
              {tk("agente.kicker", "PULSO33 Products · Our first SaaS")}
            </span>
          </div>
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[1.0] max-w-5xl">
            {tk("agente.hero.a", "Meet ")}
            <span className="gold-gradient">AgentE</span>
            {tk("agente.hero.b", " — reimagining how businesses sell on the internet.")}
          </h1>
          <p className="mt-8 text-lg md:text-xl text-muted-foreground max-w-3xl leading-relaxed">
            {tk("agente.hero.lead.1", "AgentE is the ")}
            <span className="highlight-yellow text-foreground font-medium">{tk("agente.hero.lead.os", "operating system")}</span>
            {tk("agente.hero.lead.2", " of a service business. A 24/7 AI sales agent, a multilingual website, CRM, bookings, Stripe payments and a marketplace of professionals — in one platform. Live in days, not months.")}
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href={AGENTE_URL}
              target="_blank"
              rel="noreferrer noopener"
              className="group inline-flex items-center gap-3 rounded-full bg-gold text-primary-foreground font-semibold px-8 py-4 text-xs tracking-[0.3em] uppercase hover:bg-gold-soft transition-colors"
            >
              {tk("agente.cta.visit", "Visit AgentE")}{" "}
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 rounded-full border border-gold/50 text-foreground px-8 py-4 text-xs tracking-[0.3em] uppercase hover:border-gold hover:text-gold transition-colors"
            >
              {tk("agente.cta.demo", "Book a demo")}
            </Link>
          </div>

          <div className="mt-16 grid grid-cols-3 gap-6 max-w-2xl">
            {stats.map((s) => (
              <div key={s.l}>
                <div className="font-display text-4xl md:text-5xl gold-gradient">{s.v}</div>
                <div className="mt-3 text-[10px] tracking-[0.3em] uppercase text-muted-foreground">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="border-b border-gold/15 bg-card/30">
        <div className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-14 items-start">
          <div>
            <span className="text-xs tracking-[0.4em] uppercase text-gold">
              {tk("agente.why.kicker", "Why we built it")}
            </span>
            <h2 className="font-display text-4xl md:text-5xl mt-5">
              {tk("agente.why.title", "Service businesses lose deals every single day.")}
            </h2>
          </div>
          <div className="space-y-6">
            {[
              { t: tk("agente.why.1.t", "Leads slip away after hours"), d: tk("agente.why.1.d", "60% of inquiries arrive at night or on weekends. With no one to answer, they go to the competitor.") },
              { t: tk("agente.why.2.t", "Your website doesn't sell"), d: tk("agente.why.2.d", "It's a static brochure — no lead capture, no checkout, no bookings, no translations.") },
              { t: tk("agente.why.3.t", "A broken stack"), d: tk("agente.why.3.d", "WhatsApp, Excel, Calendly, Mailchimp, Stripe… disconnected tools that duplicate work and waste hours.") },
            ].map((p) => (
              <div key={p.t} className="rounded-2xl border border-gold/20 bg-background/40 p-6">
                <h3 className="font-display text-xl text-foreground">{p.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="border-b border-gold/15">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="text-center mb-14">
            <span className="text-xs tracking-[0.4em] uppercase text-gold">{tk("agente.feat.kicker", "One platform · Everything connected")}</span>
            <h2 className="font-display text-4xl md:text-6xl mt-4">
              {tk("agente.feat.title", "What AgentE includes")}
            </h2>
            <p className="mt-6 text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              {tk("agente.feat.sub", "AI, website, CRM, bookings, payments and a professional marketplace — multi-brand and white-label from day one.")}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map(({ i: Icon, t, d }) => (
              <div key={t} className="rounded-2xl border border-gold/20 bg-card/30 p-7 hover:border-gold/60 transition-colors">
                <Icon className="text-gold" size={26} strokeWidth={1.4} />
                <h3 className="mt-5 font-display text-xl">{t}</h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CHAT MOCK */}
      <section className="border-b border-gold/15 bg-card/30">
        <div className="max-w-5xl mx-auto px-6 py-24">
          <div className="text-center mb-12">
            <span className="text-xs tracking-[0.4em] uppercase text-gold">{tk("agente.chat.kicker", "A real conversation")}</span>
            <h2 className="font-display text-4xl md:text-5xl mt-4">
              {tk("agente.chat.title", "Sells for you. While you sleep.")}
            </h2>
          </div>
          <div className="rounded-3xl border border-gold/25 bg-background/60 backdrop-blur-md p-6 md:p-10 space-y-4">
            {[
              { who: "user", text: tk("agente.chat.m1", "Hi, I need help managing my mother's estate.") },
              { who: "ai", text: tk("agente.chat.m2", "I'm very sorry. I'd recommend our Estates package (€450), it includes will reading and partition. Shall we book a consultation this week?") },
              { who: "user", text: tk("agente.chat.m3", "Yes, tomorrow afternoon.") },
              { who: "ai", text: tk("agente.chat.m4", "Perfect — here are the available slots. I'll confirm by email 👇") },
            ].map((m, i) => (
              <div key={i} className={`flex ${m.who === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-5 py-3 text-sm leading-relaxed ${
                  m.who === "user"
                    ? "bg-gold/15 border border-gold/40 text-foreground"
                    : "bg-background border border-gold/20 text-foreground/90"
                }`}>
                  {m.who === "ai" && (
                    <div className="text-[10px] tracking-[0.3em] uppercase text-gold mb-1.5 flex items-center gap-2">
                      <Sparkles size={11} /> AgentE
                    </div>
                  )}
                  {m.text}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING / INCLUDED */}
      <section className="border-b border-gold/15">
        <div className="max-w-6xl mx-auto px-6 py-24 grid md:grid-cols-2 gap-10">
          <div className="rounded-3xl border border-gold/30 p-10 bg-card/40">
            <div className="text-xs tracking-[0.3em] uppercase text-gold">
              {tk("agente.price.kicker", "Simple model")}
            </div>
            <h3 className="font-display text-4xl mt-4">
              {tk("agente.price.title", "One price. Everything included.")}
            </h3>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              {tk("agente.price.sub", "Annual subscription — no surprises. Setup in 4 hours, full onboarding in 5 days.")}
            </p>
            <div className="mt-8 flex items-end gap-3">
              <span className="font-display text-6xl gold-gradient">€1,500</span>
              <span className="text-sm text-muted-foreground mb-2">{tk("agente.price.per", "/ brand / year")}</span>
            </div>
            <div className="mt-6 text-xs text-muted-foreground/80">
              {tk("agente.price.note", "Stripe fees on marketplace transactions. No commitment.")}
            </div>
            <a
              href={AGENTE_URL}
              target="_blank"
              rel="noreferrer noopener"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-gold text-primary-foreground font-semibold px-7 py-3 text-xs tracking-[0.3em] uppercase hover:bg-gold-soft transition-colors"
            >
              {tk("agente.price.cta", "Explore AgentE")} <ArrowRight size={13} />
            </a>
          </div>
          <div>
            <span className="text-xs tracking-[0.3em] uppercase text-gold">
              {tk("agente.inc.kicker", "What you get")}
            </span>
            <h3 className="font-display text-3xl md:text-4xl mt-4 mb-8">
              {tk("agente.inc.title", "A full digital operation, ready in a week.")}
            </h3>
            <ul className="space-y-4">
              {included.map((line) => (
                <li key={line} className="flex items-start gap-3">
                  <span className="mt-1 rounded-full bg-gold/15 p-1 border border-gold/40">
                    <Check size={12} className="text-gold" />
                  </span>
                  <span className="text-sm text-foreground/85 leading-relaxed">{line}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10 grid grid-cols-3 gap-4">
              {[
                { i: Zap, l: tk("agente.badge.fast", "5-day go-live") },
                { i: ShieldCheck, l: tk("agente.badge.own", "White-label & yours") },
                { i: MessageSquare, l: tk("agente.badge.lang", "Multilingual") },
              ].map(({ i: Icon, l }) => (
                <div key={l} className="rounded-xl border border-gold/20 bg-card/30 p-4 text-center">
                  <Icon className="text-gold mx-auto" size={20} strokeWidth={1.4} />
                  <div className="mt-2 text-[10px] tracking-[0.25em] uppercase text-muted-foreground">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: `url(${hero.url})` }} aria-hidden />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/85 to-background/60" aria-hidden />
        <div className="relative max-w-4xl mx-auto px-6 py-24 text-center">
          <span className="text-xs tracking-[0.4em] uppercase text-gold">
            {tk("agente.cta.kicker", "Ready to see it live?")}
          </span>
          <h2 className="font-display text-5xl md:text-7xl mt-5">
            {tk("agente.cta.title", "Your business, running on AgentE.")}
          </h2>
          <p className="mt-6 text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {tk("agente.cta.text", "A 20-minute call and we'll show you AgentE running in a real business. If it fits, you're in production within a week.")}
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <a
              href={AGENTE_URL}
              target="_blank"
              rel="noreferrer noopener"
              className="rounded-full bg-gold text-primary-foreground px-9 py-4 text-xs tracking-[0.3em] uppercase font-semibold hover:bg-gold-soft transition-colors"
            >
              {tk("agente.cta.visit2", "Visit agente.pulso33.com")}
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full border border-gold/50 px-9 py-4 text-xs tracking-[0.3em] uppercase hover:border-gold hover:text-gold transition-colors"
            >
              {tk("agente.cta.talk", "Talk to PULSO33")}
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
